import { format } from "echarts/core";
import type { ChartSpec } from "../../types";
import { buildBoxPlotOption } from "./options/boxPlot";
import { buildBubbleOption } from "./options/bubble";
import { buildCandlestickOption } from "./options/candlestick";
import { buildCartesianOption } from "./options/cartesian";
import { createChartContext } from "./options/context";
import { buildHeatmapOption } from "./options/heatmap";
import { buildPieOption } from "./options/pie";
import { buildPolarAreaOption } from "./options/polarArea";
import { buildRadarOption } from "./options/radar";
import { buildRadialBarOption } from "./options/radialBar";
import { buildSankeyOption } from "./options/sankey";
import { buildScatterOption } from "./options/scatter";
import { buildTreemapOption } from "./options/treemap";

function wrapTooltip(text: string, width: number): string {
  const maxWidth = Math.max(40, width - 48);
  const measure = (value: string) =>
    format.getTextRect(value, "14px sans-serif").width;
  return text
    .split("\n")
    .flatMap((line) => {
      const lines: string[] = [];
      let rest = line;
      while (rest.length > 1 && measure(rest) > maxWidth) {
        let end = rest.length - 1;
        while (end > 1 && measure(rest.slice(0, end)) > maxWidth) end--;
        const space = rest.lastIndexOf(" ", end);
        if (space > 0) end = space;
        lines.push(rest.slice(0, end));
        rest = rest.slice(end).trimStart();
      }
      return [...lines, rest];
    })
    .join("\n");
}

function createChartOption(
  spec: ChartSpec,
  isDark: boolean,
  width: number,
  translateNode: (text: string) => string,
): Record<string, unknown> {
  const context = createChartContext(spec, isDark, width, translateNode);
  if (spec.chartType === "pie" || spec.chartType === "donut")
    return buildPieOption(context);
  if (spec.chartType === "radialBar") return buildRadialBarOption(context);
  if (spec.chartType === "sankey") return buildSankeyOption(context);
  if (spec.chartType === "polarArea") return buildPolarAreaOption(context);
  if (spec.chartType === "radar") return buildRadarOption(context);
  if (spec.chartType === "treemap") return buildTreemapOption(context);
  if (spec.chartType === "scatter") return buildScatterOption(context);
  if (spec.chartType === "bubble") return buildBubbleOption(context);
  if (spec.chartType === "candlestick") return buildCandlestickOption(context);
  if (spec.chartType === "boxPlot") return buildBoxPlotOption(context);
  if (spec.chartType === "heatmap") return buildHeatmapOption(context);
  return buildCartesianOption(context);
}

export function buildOption(
  spec: ChartSpec,
  isDark: boolean,
  width = 600,
  translateNode: (text: string) => string = (text) => text,
): object {
  const option = createChartOption(
    spec,
    isDark,
    width,
    translateNode,
  ) as Record<string, unknown>;
  // Avoid replaying long chart sweeps during live data and container updates.
  option.animation =
    typeof window === "undefined" ||
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  option.animationDuration = 200;
  option.animationDurationUpdate = 200;
  option.animationEasing = "cubicOut";
  option.animationEasingUpdate = "cubicOut";
  const tooltip = option.tooltip;
  if (tooltip && typeof tooltip === "object" && !Array.isArray(tooltip)) {
    const settings = tooltip as Record<string, unknown>;
    settings.renderMode = "richText";
    settings.confine = true;
    const formatter = settings.formatter;
    if (typeof formatter === "function") {
      settings.formatter = (...args: unknown[]) => {
        const content = formatter(...args);
        return typeof content === "string"
          ? wrapTooltip(content, width)
          : content;
      };
    }
  }
  return option;
}
